
/* eslint guard-for-in: 0 */
/* eslint no-restricted-syntax: 0 */
import Mixpanel from 'common/services/stats/-RFC.Mixpanel';
import { MeRepository } from 'common/repositories/index';
import Cookie from 'common/services/util/-RFC.Cookie';
import { Session, SessionKeys } from 'common/services/-RFC.session.js';

const _serviceDependencies = [
    'Auth', '$location', '$state', '$window', 'ErrorHandler', 'OAuthRestService', '$rootScope',
    'JWT', 'Util', '$q', 'Facebook', 'Google',
];

/**
 * @ngdoc service
 * @name services.Sso
 *
 * @description
 * This class is used to save extra params
 * received in the auth url and redirect to the received uri after login
 */
class Sso {

    /**
     * @param {any} Auth Auth
     * @param {any} $location $location
     * @param {any} $state $state
     * @param {any} $window config
     * @param {any} ErrorHandler config
     * @param {any} OAuthRestService config
     * @param {any} $rootScope $rootScope
     * @param {any} JWT JWT
     * @param {any} $q $q
     * @param {any} Facebook Facebook
     * @param {any} Google Google
     */
    constructor(
        Auth, $location, $state,
        $window, ErrorHandler, OAuthRestService, $rootScope, JWT, Util, $q,
        Facebook, Google,
    ) {

        this.Auth = Auth;
        this.Util = Util;
        this.$location = $location;
        this.$window = $window;
        this.ErrorHandler = ErrorHandler;
        this.OAuthRestService = OAuthRestService;
        this.$rootScope = $rootScope;
        this.JWT = JWT;
        this.$q = $q;
        this.$state = $state;
        this.Facebook = Facebook;
        this.Google = Google;

        // If the token contains the agent id set it to session storage
        // in order to display the otc tab from subscriptions page for support agents only
        const queryParams = this.$location.search();
        const token = queryParams.access_token;

        if (token) {

            const decodedToken = this.JWT.getTokenPayload(token);
            if ('aid' in decodedToken) {

                sessionStorage.setItem('aid', decodedToken.aid);

            }

        }

    }

    /**
     * @ngdoc function
     * @name services.Sso#saveExtraParams
     * @methodOf services.saveExtraParams
     * @function
     *
     * @description
     * This method is used to saveExtraParams from the auth URL
     *
     */
    saveExtraParams() {

        const queryParams = this.$location.search();
        const path = this.$location.path();

        // save extra params in to session to use them even if the url changes
        Session.setValue(SessionKeys.SsoExtraUrlParams, queryParams);

        /* save client_id, campaign, content_id and campaign_type and redirect_uri,
           in session storage to be saved after refresh */
        const ssoSessionStorage = sessionStorage.getItem('SsoExtraUrlParams');
        if (!ssoSessionStorage || ssoSessionStorage === '{}') {

            const ssoExtraUrlParams = {
                client_id: queryParams.client_id,
                campaign: queryParams.campaign,
                content_id: queryParams.content_id,
                campaign_type: queryParams.campaign_type,
                redirect_uri: queryParams.redirect_uri,
                cw_ref_code: queryParams.cw_ref_code,
            };

            if (queryParams.action !== 'gotologin') {

                ssoExtraUrlParams.action = queryParams.action;

            }
            sessionStorage.setItem('SsoExtraUrlParams', JSON.stringify(ssoExtraUrlParams));

        }

        if ((queryParams.src || '').toLowerCase() === 'mobileapp') {

            document.cookie = 'oe_mobile_session=1; path=/; SameSite=Strict; Secure';

        }

        /* the SSO link is not a thirdParyLogin if the URL contains
           a client id and the path is /auth/reset */
        if (queryParams.client_id && path !== '/auth/reset') {

            Session.setValue(SessionKeys.ThirdPartyLogin, true);
            this.$rootScope.State.isThirdPartyLogin = true;

        }

        // Always ask for login, clean cookies for testing purposes only
        // or the clients that need this feature
        // ( to be able to switch users in the Alexa app for tests enable this in the config)
        if (this.getClientConfig('removeCookie') === true) {

            this.Auth.removeLoginCookies();

        }

        // Save token and device id in session storage for launcher forgot flow
        const tokenPayload = this.JWT.getTokenPayload(queryParams.anon_token) ||
                            this.JWT.getTokenPayload(queryParams.access_token);

        if (tokenPayload && tokenPayload.anon === true && tokenPayload.cid === 'launcher' && tokenPayload.did) {

            localStorage.setItem('ConnectClientToken', queryParams.anon_token || queryParams.access_token);
            localStorage.setItem('ConnectClientDeviceID', JSON.stringify(tokenPayload.did));

        }

    }

    /**
     * @ngdoc function
     * @name services.Sso#exchangeToken
     * @methodOf services.exchangeToken
     * @function
     *
     * @description
     * This method is used to change the received token with a dashboard token
     *
     */
    exchangeToken() {

        this.echangePromise = this.$q.defer();

        const queryParams = this.$location.search();

        // set the anon_token in cookies received as param to be used in the register flow
        if (queryParams.anon_token || queryParams.access_token) {

            const tokenPayload = {
                data: {
                    access_token: queryParams.anon_token || queryParams.access_token,
                },
            };

            const tokenData = this.JWT.getTokenPayload(queryParams.anon_token) ||
                              this.JWT.getTokenPayload(queryParams.access_token);

            // if the token clientId is not dashboard exhange token
            if (tokenData.cid !== 'dashboard' && tokenData.scope !== 'passwords') {

                // get a new token for dashboard
                this.getClientToken('dashboard', tokenPayload.data.access_token, queryParams.response_type).then((response) => {

                    const oauthResponse = response.data;
                    const dashboardTokenPayload = {
                        data: {
                            access_token: oauthResponse.access_token,
                            refresh_token: oauthResponse.refresh_token,
                            expires_in: oauthResponse.expires_in,
                        },
                    };
                    this.Auth.setLoginCookies(dashboardTokenPayload);
                    this.redirectToAuth();

                }).catch((exception) => {

                    const errorLocation = 'Sso.saveExtraParams Error';

                    this.ErrorHandler.handleError(exception, errorLocation);

                    this.Auth.removeLoginCookies();
                    this.redirectToAuth();

                });

            } else {

                this.Auth.setLoginCookies(tokenPayload);
                this.redirectToAuth(tokenData.scope);

            }

        } else {

            this.redirectToAuth();

        }


        return this.echangePromise.promise;

    }


    /**
     * @ngdoc function
     * @name services.Sso#redirectToAuth
     * @methodOf services.redirectToAuth
     * @function
     *
     * @description
     * This method is used to clear any jwt token from params
     * and redirect to login or register controller
     *
     * @param
     * String - Describe token scope
     */
    redirectToAuth(tokenScope) {

        const queryParams = this.$location.search();
        // remove the anon_token param so that second time will go on with the redirect
        delete queryParams.anon_token;
        delete queryParams.access_token;
        Session.setValue(SessionKeys.SsoExtraUrlParams, queryParams);

        this.echangePromise.resolve(tokenScope);

    }

    /**
     * @ngdoc function
     * @name services.Sso#getAnonToken
     * @methodOf services.getAnonToken
     * @function
     *
     * @description
     * This method is used to get the anon token received as parameter
     *
     */
    getAnonToken() {

        const queryParams = Session.getValue(SessionKeys.SsoExtraUrlParams);

        if (queryParams.anon_token || queryParams.access_token) {

            return queryParams.anon_token || queryParams.access_token;

        }

        return false;

    }

    /**
     * @ngdoc function
     * @name services.Sso#showCloseButton
     * @methodOf services.showCloseButton
     * @function
     *
     * @description
     * This method is used to check if the close button need to be visible in the login or register
     *
     */
    getCloseButton() {

        const queryParams = Session.getValue(SessionKeys.SsoExtraUrlParams);

        if (queryParams.close_button) {

            return queryParams.close_button;

        }

        return false;

    }

    /**
     * @ngdoc function
     * @name services.Sso#getClientId
     * @methodOf services.getClientId
     * @function
     *
     * @description
     * This method is used to get the client_id received as param in url
     *
     */
    getClientId() {

        const queryParams = Session.getValue(SessionKeys.SsoExtraUrlParams);

        return queryParams.client_id || '';

    }

    /**
     * @ngdoc function
     * @name services.Sso#getClientConfig
     * @methodOf services.getClientConfig
     * @function
     *
     * @description
     * This method is used to check get the configs for the current client_id
     * If the feature param is set it will return the feature configuration
     *
     */
    getClientConfig(feature) {

        const ssoClientConfig = CWCONFIG.ssoConfig[this.getClientId()];
        if (ssoClientConfig && feature) {

            return ssoClientConfig[feature];

        }

        return ssoClientConfig;

    }

    /**
     * @ngdoc function
     * @name services.Sso#showSkipButton
     * @methodOf services.showSkipButton
     * @function
     *
     * @description
     * This method is used to check if the skip button need to be visible in the login or register
     *
     */
    getSkipUri() {

        const skipUri = this.getClientConfig('skip_uri');
        return skipUri || false;

    }

    /**
     * @ngdoc function
     * @name services.Sso#showChangeLang
     * @methodOf services.showChangeLang
     * @function
     *
     * @description
     * This method is used to check if the change language param
     *
     */
    getChangeLang() {

        const queryParams = Session.getValue(SessionKeys.SsoExtraUrlParams);

        if (queryParams.change_lang && queryParams.change_lang === 'false') {

            return false;

        }

        return true;

    }

    /**
     * @ngdoc function
     * @name services.Sso#getSocialLogin
     * @methodOf services.getSocialLogin
     * @function
     *
     * @description
     * This method is used to check social login param
     *
     */
    getSocialLogin() {

        const queryParams = Session.getValue(SessionKeys.SsoExtraUrlParams);

        if (queryParams.social_login && queryParams.social_login === 'false') {

            return false;

        }

        return true;

    }

    /**
     * @ngdoc function
     * @name services.Sso#getEmbeddedSocialLogin
     * @methodOf services.getEmbeddedSocialLogin
     * @function
     *
     * @description
     * This method is used to check the embedded social login param
     *
     */
    getEmbeddedSocialLogin() {

        const queryParams = Session.getValue(SessionKeys.SsoExtraUrlParams);

        return queryParams.embedded_social && queryParams.embedded_social === 'true';

    }

    /**
     * @ngdoc function
     * @name services.Sso#getEmailAddress
     * @methodOf services.getEmailAddress
     * @function
     *
     * @description
     * This method is used to check if the email_adreess param exists
     *
     */
    getEmailAddress() {

        const queryParams = Session.getValue(SessionKeys.SsoExtraUrlParams);

        return queryParams.email_address || '';

    }

    /**
     * @ngdoc function
     * @name services.Sso#getDecodedEmailAddress
     * @methodOf services.getDecodedEmailAddress
     * @function
     *
     * @description
     * This method is used to check if the email param exists and decodes it
     *
     */
    getDecodedEmailAddress() {

        const queryParams = Session.getValue(SessionKeys.SsoExtraUrlParams);

        const emailAddress = (queryParams.email) ? this.Util.base64decode(queryParams.email) : '';

        return emailAddress;

    }

    /**
     * @ngdoc function
     * @name services.Sso#isThirdPartyLogin
     * @methodOf services.isThirdPartyLogin
     * @function
     *
     * @description
     * This method is used to check if the login is a thirdParty login
     *
     */
    isThirdPartyLogin() {

        return Session.getValue(SessionKeys.ThirdPartyLogin);

    }

    /**
     * @ngdoc function
     * @name services.Sso#redirect
     * @methodOf services.redirect
     * @function
     *
     * @description
     * This method is used to redirect to the received uri in the auth url
     *
     */
    redirect() {

        // thirdparty login
        if (this.isThirdPartyLogin()) {

            const queryParams = Session.getValue(SessionKeys.SsoExtraUrlParams);
            const user = MeRepository.getMyUser();
            const { language } = user.attributes;

            this.getClientToken(queryParams.client_id, null, queryParams.response_type)
                .then((response) => {

                    const oauthResponse = response.data;
                    // set # as parameter separator for Alexa
                    const separator = (queryParams.client_id === 'alexa' ? '#' : '?');
                    let urlParams = '';

                    // Append received params and the oauth params to the redirect_uri
                    if (this.getClientConfig('appendUrlParams') === true) {

                        // append received params to the redirect_uri
                        angular.forEach(queryParams, (value, key) => {

                            // ignore redirect_uri and client_id these two won't be appended
                            if (key !== 'redirect_uri' && key !== 'client_id') {

                                urlParams += `${urlParams.indexOf(separator) === -1 ? separator : '&'}${key}=${value}`;

                            }

                        });

                        // append to the redirect url the token,
                        // expiring time, refresh token and token type
                        angular.forEach(oauthResponse, (value, key) => {

                            // ignore redirect_uri this won't be appended
                            if (key !== 'redirect_uri') {

                                urlParams += `${urlParams.indexOf(separator) === -1 ? separator : '&'}${key}=${value}`;

                            }

                        });

                    }

                    Mixpanel.send('ConnectWeb Auth Login/Register/Forgot/Set Password Open', {
                        Source: 'Login ',
                        'Redirect Client': queryParams.client_id || 'None',
                    });

                    // Clean cookies for testing purposes only
                    // or the clients that need this feature
                    // ( to be able to switch users in the Alexa app
                    // for tests enable this in the config)
                    if (this.getClientConfig('removeCookie') === true) {

                        this.Auth.removeLoginCookies();

                    }

                    // disable sso redirect for spotlight using backend redirect_uri
                    if (queryParams.client_id === 'spotlight' && queryParams.embedded === 'false') {

                        this.$location.path('/dashboard/devices');
                        angular.element('body').removeClass('spotlight');

                        return;

                    }

                    // Match received redirect_uri with the client_id uri configured in backend
                    // Only for alexa and for backwards compatibility
                    // If we don't receive any redirect_uri we check the one sent by the backend
                    // otherwise we redirect user to dashboard
                    if (
                        queryParams.redirect_uri &&
                        this.matchDomain(queryParams.redirect_uri, oauthResponse.redirect_uri)
                    ) {

                        this.setClientCookies(queryParams.client_id, oauthResponse);
                        let redirectUrl = queryParams.redirect_uri;
                        if (this.getClientConfig('appendUrlParams') === true) {

                            redirectUrl += urlParams;

                        }

                        // dashboard SSO
                        if (queryParams.client_id === 'dashboard') {

                            this.$window.location.href = `/${language}${redirectUrl}`;

                        } else {

                            this.$window.location.href = redirectUrl;

                        }

                    } else if (oauthResponse.redirect_uri && oauthResponse.redirect_uri !== '') {

                        this.setClientCookies(queryParams.client_id, oauthResponse);
                        let redirectUrl = oauthResponse.redirect_uri;

                        if (this.getClientConfig('appendUrlParams') === true) {

                            redirectUrl += urlParams;

                        }

                        // dashboard rules for links without "redirect_uri" params
                        if (queryParams.client_id === 'dashboard' &&
                            oauthResponse.redirect_uri.indexOf('dashboard') > -1) {

                            // auth reset password rule
                            if (this.$location.path().indexOf('/auth/reset') > -1) {

                                const getParams = this.Util.getParams(this.$location.url());
                                const resetToken = getParams ? (getParams.anon_token || getParams.access_token) : '';

                                this.$window.location.href = `/${language}/auth/reset/${resetToken}`;

                            } else if (queryParams['x-a-medium']) {

                                this.$state.go('root.dashboard.home.ltb', {
                                    resourceId: queryParams['x-a-medium'],
                                    ltb: 'welcome-modal',
                                });

                            } else {

                                // general rule for rest of the links
                                this.$window.location.href = `/${language}/dashboard`;

                            }

                        } else if (queryParams.client_id === 'unicorn' || queryParams.client_id === 'avira-macsupport') {

                            this.$state.go('root.dashboard.home');
                            this.$window.location.href = redirectUrl;

                        } else {

                            this.$window.location.href = redirectUrl;

                        }

                    } else {

                        // disable SSO for the next calls
                        Session.setValue(SessionKeys.ThirdPartyLogin, false);

                        // display the prime trial modal for social login
                        if (queryParams['x-a-medium']) {

                            this.$state.go('root.dashboard.home.ltb', {
                                resourceId: queryParams['x-a-medium'],
                                ltb: 'welcome-modal',
                            });

                        } else {

                            // redirect to dashboard if is not matched
                            this.$location.path('/dashboard');

                        }


                    }

                }).catch((exception) => {

                    const errorLocation = 'Sso.redirect Error';

                    this.ErrorHandler.handleError(exception, errorLocation);

                    this.Auth.removeLoginCookies();

                    this.$location.path('/auth/login');

                });

        }

    }

    /**
     * @ngdoc function
     * @name services.Sso#setClientCookies
     * @methodOf services.setClientCookies
     * @function
     *
     * @description
     * This method is used to set client cookies
     *
     */
    setClientCookies(clientId, data) {

        const {
            domain,
            rootDomain,
            ssoToken,
        } = CWCONFIG.cookie;

        // the expire time is received in seconds and we convert it to days
        const tokenExpireInDays = parseInt(data.expires_in, 10) / 60 / 60 / 24;

        // Cookies to store jwt token
        const ssoTokenCookie = `${ssoToken}${clientId}_sso`;
        Cookie.set(ssoTokenCookie, data.access_token, {
            expires: Math.round(tokenExpireInDays) || 1,
            path: '/',
            domain: rootDomain || domain,
        });
        // Cookies to store token expiration time
        const ssoTokenExpiresInCookie = `${ssoToken}${clientId}_lifetime_sso`;
        Cookie.set(ssoTokenExpiresInCookie, data.expires_in, {
            expires: Math.round(tokenExpireInDays) || 1,
            path: '/',
            domain: rootDomain || domain,
        });
        // Cookies to store refresh_token
        const ssoRefreshTokenCookie = `${ssoToken}${clientId}_refresh_sso`;
        Cookie.set(ssoRefreshTokenCookie, data.refresh_token, {
            expires: Math.round(tokenExpireInDays) || 1,
            path: '/',
            domain: rootDomain || domain,
        });

    }

    /**
     * @ngdoc function
     * @name services.Sso#enableAutoLogon
     * @methodOf services.enableAutoLogon
     * @function
     *
     * @description
     * This method is used to check if autologon using
     * the existing token is enabled or disabled for that clientId
     *
     */
    enableAutoLogon() {

        if (this.getClientConfig('disableSsoAutoLogin') === true) {

            return false;

        }

        return true;

    }

    /**
     * @ngdoc function
     * @name services.Sso#getClientToken
     * @methodOf services.Sso
     *
     * @description
     * Get client token for thirdparty login using the dashboard token
     */
    getClientToken(clientId, token, responseType) {

        const oauthPayload = {
            grant_type: 'client_token',
            client_id: clientId,
            response_type: responseType,
        };

        const oauthHeader = {
            'X-Avira-Token': this.Auth.getTfaCookie(),
        };

        if (token) {

            oauthHeader.Authorization = `Bearer ${token}`;

        }

        return this.OAuthRestService.post(oauthPayload, oauthHeader);

    }

    /**
     * @ngdoc function
     * @name services.Sso#getParams
     * @methodOf services.Sso
     *
     * @description
     * Get received params
     */
    getParams() {

        const queryParams = Session.getValue(SessionKeys.SsoExtraUrlParams);
        return queryParams;

    }

    /**
     * $ngdoc function
     * @name services.Sso#matchDomain
     * @methodOf services.Sso
     *
     * @description
     * Validate a redirect target against the backend-registered redirect_uri.
     * The requested target must match the registered one EXACTLY on scheme,
     * host and path. No pattern/substring matching: a URL that merely contains
     * the registered value (e.g. https://attacker.example/x/https://trusted/y)
     * must NOT be accepted, otherwise session tokens can be leaked to an
     * attacker-controlled host (account takeover).
     */
    matchDomain(url, urlToBeMatched) {

        if (!url || !urlToBeMatched) {

            return false;

        }

        try {

            const target = this.parseUrl(url);
            const registered = this.parseUrl(urlToBeMatched);

            return target.protocol === registered.protocol &&
                target.host === registered.host &&
                target.pathname === registered.pathname;

        } catch (e) {

            return false;

        }

    }

    /**
     * $ngdoc function
     * @name services.Sso#parseUrl
     * @methodOf services.Sso
     *
     * @description
     * Parse a URL using a DOM anchor element. Intentionally avoids the `URL`
     * constructor, which is not available in IE11. Returns normalised scheme,
     * host and path for an exact comparison.
     */
    parseUrl(value) {

        const anchor = document.createElement('a');
        anchor.href = String(value);

        // IE11 omits the leading slash on anchor.pathname; normalise it.
        let pathname = anchor.pathname || '';
        if (pathname.charAt(0) !== '/') {

            pathname = `/${pathname}`;

        }

        return {
            protocol: (anchor.protocol || '').toLowerCase(),
            host: (anchor.host || '').toLowerCase(),
            pathname,
        };

    }

}

_serviceDependencies.push(Sso);

angular.module('services')
    .service('Sso', _serviceDependencies);



// WEBPACK FOOTER //
// ./scripts/common/services/Sso.js